import React, { useState, useEffect } from "react";
import { forgotPassword } from "../../../api/auth";
import { useToast } from "../../../context/ToastContext";
import AuthPageBanner from "../../../assets/AuthPageBanner.png";

const logimg = AuthPageBanner;

export default function ForgotPassword({ onGoToLogin, onClose }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [emailSent, setEmailSent] = useState(false);
  const { showSuccess, showError } = useToast();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setErrors((prev) => ({ ...prev, [name]: "" }));
    setEmail(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!emailRegex.test(email)) {
      newErrors.email = "Please enter a valid email address.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const response = await forgotPassword(email);
      showSuccess(
        response.message || "Password reset link sent to your email!",
      );
      setEmailSent(true);
      setErrors({});
    } catch (err) {
      setErrors({
        general: err.message || "Failed to send reset link. Please try again.",
      });
      showError(err.message || "Failed to send reset link.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col-reverse items-center md:flex-row-reverse rounded-2xl w-full h-auto md:h-[600px] overflow-hidden p-4 md:p-0">
      {/* Form Side */}
      {!emailSent ? (
        <div className="w-full md:w-1/2 p-4 md:p-8 space-y-4 overflow-y-auto">
          <div>
            <h2
              className="text-xl md:text-2xl font-semibold text-gray-700 leading-tight"
              style={{ fontFamily: "CabinetGrotesk-Bold" }}
            >
              Forgot Password?
            </h2>
            <p
              className="text-sm text-black pt-4 leading-normal"
              style={{ fontFamily: "CabinetGrotesk-Regular" }}
            >
              Enter your email address and we'll send you a link to reset your
              password
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email Input */}
            <div className="relative w-full">
              <input
                id="email"
                type="email"
                name="email"
                value={email}
                onChange={handleChange}
                placeholder=" "
                className="peer w-full rounded-lg border border-gray-300 bg-white px-3 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF4C18]"
                disabled={loading}
              />
              <label
                htmlFor="email"
                className="absolute left-3 top-[-8px] bg-white px-1 text-[11px] md:text-xs text-[#FF4C18] transition-all
              peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-gray-400 peer-placeholder-shown:text-sm
              peer-focus:top-[-8px] peer-focus:text-[11px] md:peer-focus:text-xs peer-focus:text-[#FF4C18]"
              >
                Email Address
              </label>
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>

            {/* General Error */}
            {errors.general && (
              <p className="text-red-500 text-xs">{errors.general}</p>
            )}

            {/* Back to Login */}
            {/* <div className="flex justify-end">
            <button
              type="button"
              onClick={onGoToLogin}
              className="text-sm text-[#FF4C18] hover:underline"
            >
              Back to Sign In
            </button>
          </div> */}

            {/* Submit Button */}
            <button
              type="submit"
              className={`w-full bg-[#FF4C18] text-white py-2 rounded-lg hover:scale-105 transition flex items-center justify-center ${
                loading ? "opacity-60 cursor-not-allowed" : ""
              }`}
              disabled={loading}
            >
              {loading ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5 mr-2 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    ></path>
                  </svg>
                  Sending Reset Link...
                </>
              ) : (
                "Send Reset Link"
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center my-6">
            <hr className="flex-1 border-gray-300" />
            <span className="px-4 text-sm text-gray-400">Or</span>
            <hr className="flex-1 border-gray-300" />
          </div>

          {/* Register Link */}
          <p className="text-sm text-center text-gray-500">
            Remember your password?{" "}
            <button
              onClick={onGoToLogin}
              className="text-[#FF4C18] hover:underline font-medium"
            >
              Sign In
            </button>
          </p>
        </div>
      ) : (
        <div className="w-full md:w-1/2 p-4 md:p-8 space-y-4 overflow-y-auto">
          <div>
            <h2
              className="text-xl md:text-2xl font-semibold text-black leading-tight"
              style={{ fontFamily: "CabinetGrotesk-Bold" }}
            >
              Check Your Email
            </h2>
            <p
              className="text-sm text-black pt-4 leading-normal"
              style={{ fontFamily: "CabinetGrotesk-Regular" }}
            >
              We've sent a password reset link to your email. Follow the
              instructions in the email to create a new password.
            </p>
          </div>
          <button
            type="submit"
            onClick={onGoToLogin}
            className={`w-full bg-[#FF4C18] text-white py-2 rounded-lg hover:scale-105 transition flex items-center justify-center `}
          >
            Back To Login
          </button>
        </div>
      )}

      {/* <!-- image --> */}
      <div className="hidden md:block w-full md:w-1/2 h-[300px] md:h-full relative rounded-2xl">
        <img
          className="w-full h-full object-cover rounded-2xl"
          alt="forgot password"
          src={logimg}
        />
      </div>
    </div>
  );
}
